import {
	OnInit,
	AfterViewInit,
	Component,
	ViewChild,
	Input,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { mergeMap } from 'rxjs/operators';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import Property = Components.Schemas.Location;
import { ActivatedRoute, Router } from '@angular/router';
import { rememberAddPropertyCompanyId } from 'src/app/ui/add-property/add-property-main-tab.component';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { Company } from 'src/app/models/data/company';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetRowClickDirective,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { AddressPipe } from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';
import { MatOption, MatSelect } from '@angular/material/select';

@Component({
	selector: 'app-company-properties',
	templateUrl: './company-properties.component.html',
	styleUrls: ['./company-properties.component.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		AddressPipe,
		DataSetLoadingRowComponent,
	],
})
export class CompanyPropertiesComponent implements OnInit, AfterViewInit {
	@Input() company?: Company;

	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	readonly companyId: string;
	editingCompanyId: string | undefined = '';
	activeCompanyId: string | undefined = '';

	displayedColumns = ['name', 'address', 'devices', 'active', 'rowMenu'];
	dataSource = new MatTableDataSource<{
		property: Property;
		deviceCount: number;
	}>();

	canAddProperty = false;
	companyIsSentrilock = false;

	fieldLabels = {
		defaultPlaceholder: $localize`:@@companyProperties-defaultPlaceholder:Search property name...`,
		propertyName: $localize`:@@companyProperties-propertyName:Property Name`,
		address: $localize`:@@companyProperties-address:Address`,
		townCity: $localize`:@@companyProperties-townCity:Town/City`,
		stateProvince: $localize`:@@companyProperties-stateProvince:State/Province`,
		postalCode: $localize`:@@companyProperties-postalCode:Postal Code`,
		country: $localize`:@@companyProperties-country:Country`,
		status: $localize`:@@companyProperties-status:Status`,
	};
	activeOptions: DropdownOption[] = [
		{
			label: $localize`:@@companyProperties-filterOptionActive:Active`,
			value: 'true',
		},
		{
			label: $localize`:@@companyProperties-filterOptionInactive:Inactive`,
			value: 'false',
		},
	];

	getCompanyPropertiesByCompanyIdLoadingKey =
		ApiLoadingKeys.getCompanyPropertiesByCompanyId;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.companyId = this.route.snapshot.params.id;
	}

	async ngOnInit() {
		await this.setCanAddProperty();
	}

	ngAfterViewInit(): void {
		this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.getCompanyPropertiesByCompanyId(
						this.companyId,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
	}

	onAddButtonClick(): void {
		rememberAddPropertyCompanyId(this.editingCompanyId);

		this.router.navigateByUrl('/add-property', {
			state: { companyId: this.editingCompanyId },
		});
	}

	onRowClick(property: Property): void {
		this.router.navigate([`/property-details/${property.id}`], {
			queryParams: { companyId: this.editingCompanyId },
		});
	}

	onRowDoubleClick(property: Property): void {}

	async setCanAddProperty(): Promise<void> {
		const hasPropertyManagement = this.scService.has(
			SystemCapabilityEnum.propertyManagement
		);
		const hasGlobalCompanyMgmt = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		const [sentrilockId, userProfile] = await Promise.all([
			this.apiService.getSentrilockCompanyId(),
			this.apiService.getUserProfile(),
		]);
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		this.editingCompanyId = this.company?.id;
		if (!this.editingCompanyId) {
			this.editingCompanyId = this.activeCompanyId;
		}

		const isEditingActiveCompany =
			this.activeCompanyId === this.editingCompanyId;
		this.companyIsSentrilock = this.editingCompanyId === sentrilockId;

		this.canAddProperty =
			(hasPropertyManagement && isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt &&
				!isEditingActiveCompany &&
				!this.companyIsSentrilock);
	}

	rowId(row: { property: Property; deviceCount: number }): string {
		return row?.property?.id || 'unknown-id';
	}
}
